/**
 * components/HelpBanner.jsx
 *
 * Usage:
 *   <HelpBanner
 *     storageKey="help-campaign-page"
 *     title="Advertisement Campaign"
 *     message="AI CEO has generated this campaign. Review your ad copy, preview each platform, export as PNG, and copy post text to publish to your channels."
 *   />
 *
 * When dismissed, renders nothing — but passes a "?" button up via
 * the optional onDismiss / onReopen callbacks so TopNav can show it.
 * Easier pattern: use the useHelpBanner() hook to get the TopNav slot node.
 */

import React, { useState, useCallback } from 'react';

const ACCENT = '#378add';

// ─── Hook ─────────────────────────────────────────────────────────────────────
// Returns { banner, navButton }
// Render `banner` below TopNav, render `navButton` inside TopNav's right side.

export function useHelpBanner({ storageKey, title, message }) {
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(storageKey) !== 'dismissed'; }
    catch { return true; }
  });
  const [expanded, setExpanded] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    setExpanded(false);
    try { localStorage.setItem(storageKey, 'dismissed'); } catch {}
  }, [storageKey]);

  const reopen = useCallback(() => {
    setVisible(true);
    try { localStorage.removeItem(storageKey); } catch {}
  }, [storageKey]);

  const isLong = message.length > 120;

  const banner = visible ? (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '10px 20px', borderBottom: '1px solid rgba(55,138,221,0.18)',
      background: 'rgba(55,138,221,0.06)',
    }}>
      {/* Left accent bar */}
      <div style={{ width: '3px', borderRadius: '2px', background: ACCENT, alignSelf: 'stretch', flexShrink: 0, marginTop: '1px' }} />

      {/* Icon */}
      <span style={{ fontSize: '14px', marginTop: '1px', flexShrink: 0 }}>💡</span>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <span style={{ fontSize: '12px', fontWeight: '600', color: ACCENT, marginRight: '6px' }}>
            {title}.
          </span>
        )}
        <span style={{
          fontSize: '12px', color: 'var(--ink-soft)', lineHeight: 1.5,
          display: expanded || !isLong ? 'inline' : '-webkit-box',
          WebkitLineClamp: expanded ? undefined : 1,
          WebkitBoxOrient: 'vertical',
          overflow: expanded || !isLong ? 'visible' : 'hidden',
        }}>
          {message}
        </span>
        {isLong && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
              fontSize: '11px', color: ACCENT, fontWeight: '500', fontFamily: 'var(--sans)',
            }}
          >
            {expanded ? 'Show less ▲' : 'Show more ▼'}
          </button>
        )}
      </div>

      {/* Dismiss */}
      <button
        onClick={dismiss}
        title="Dismiss (access via ? in nav)"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '14px', color: 'var(--ink-mute)', padding: '0 2px',
          lineHeight: 1, flexShrink: 0, marginTop: '1px',
        }}
      >
        ✕
      </button>
    </div>
  ) : null;

  // Small ? button to be slotted into TopNav's right side
  const navButton = !visible ? (
    <button
      onClick={reopen}
      title="Show page help"
      style={{
        width: '26px', height: '26px', borderRadius: '50%',
        background: 'none', border: `1.5px solid ${ACCENT}`,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '700', color: ACCENT,
        transition: 'all 0.15s', flexShrink: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(55,138,221,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
    >
      ?
    </button>
  ) : null;

  return { banner, navButton };
}


// ─── Standalone component (simpler, no TopNav integration) ────────────────────
export default function HelpBanner({ storageKey, title, message }) {
  const { banner } = useHelpBanner({ storageKey, title, message });
  return banner;
}
